'use client';

import { getComponentProductData } from '@/api/home';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { HOMEPREVIEWPATH } from '@/utils/constants';
import { useCallback, useEffect, useRef } from 'react';

interface UseFetchDynamicProductsParams<T> {
  componentId?: string;
  componentBlockId?: string | number;
  currencyName?: string;
  enableAuthRefetch?: boolean;
  dynamicProductsData: T | null;
  hasAttemptedFetch: boolean;
  isLoading: boolean;
  setDynamicProductsData: React.Dispatch<React.SetStateAction<T | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setHasAttemptedFetch: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useFetchDynamicProducts<T>({
  componentId,
  componentBlockId,
  currencyName,
  enableAuthRefetch = true,
  dynamicProductsData,
  hasAttemptedFetch,
  isLoading,
  setDynamicProductsData,
  setIsLoading,
  setHasAttemptedFetch,
}: UseFetchDynamicProductsParams<T>) {
  const token = useStore((state) => state.token);
  const isLoggedIn = !!token;
  const authStatusRef = useRef(isLoggedIn);

  const fetchDynamicProducts = useCallback(async () => {
    if (!componentId || !componentBlockId) {
      return;
    }

    const isPreview =
      typeof window !== 'undefined' && window.location.pathname.includes(HOMEPREVIEWPATH);

    setIsLoading(true);
    try {
      const res: any = await getComponentProductData({
        component_id: componentId,
        id: Number(componentBlockId),
        currency: currencyName || 'USD',
        status: isPreview ? 2 : 1,
      });

      if (res && typeof res !== 'string' && res.data) {
        setDynamicProductsData(res.data);
      } else {
        setDynamicProductsData(null);
      }
    } catch (err) {
      logger.error('获取商品数据失败:', err);
    } finally {
      setIsLoading(false);
      setHasAttemptedFetch(true);
    }
  }, [
    componentBlockId,
    componentId,
    currencyName,
    setDynamicProductsData,
    setHasAttemptedFetch,
    setIsLoading,
  ]);

  useEffect(() => {
    if (authStatusRef.current === isLoggedIn) {
      return;
    }

    authStatusRef.current = isLoggedIn;

    if (!enableAuthRefetch) {
      return;
    }

    if (!hasAttemptedFetch || !dynamicProductsData || isLoading) {
      return;
    }

    fetchDynamicProducts();
  }, [
    dynamicProductsData,
    enableAuthRefetch,
    fetchDynamicProducts,
    hasAttemptedFetch,
    isLoading,
    isLoggedIn,
  ]);

  return fetchDynamicProducts;
}
